import { useCallback, useEffect, useState, useTransition } from "react";

import { AnimatePresence, motion } from "framer-motion";
import type { PhotoWithSrc as FotoComSrc } from "@historia/shared/contracts";
import { useQueryClient } from "@tanstack/react-query";
import {
  AFTER_PHOTO_CHANGE,
  deletePhoto,
  deletePhotos,
  movePhotos,
  photoDownloadUrl,
  toggleFeatured,
  updatePhoto,
} from "@/api/mutations";
import { ApiError } from "@/api/client";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { longDate as dataLonga } from "@historia/shared/dates";

type MarcoResumo = { id: string; titulo: string; data: string };
type AlbumResumo = { id: string; nome: string };
type LugarResumo = { id: string; nome: string; icone: string };

/** Grelha responsiva de fotos + lightbox com navegação, edição e apagar. */
export function GrelhaAlbum({
  fotos,
  marcos,
  albuns,
  lugares = [],
  somenteLeitura = false,
}: {
  fotos: FotoComSrc[];
  marcos: MarcoResumo[];
  albuns: AlbumResumo[];
  /** Lugares do mapa, para ligar uma foto ao sítio onde foi tirada (IDEA-014). */
  lugares?: LugarResumo[];
  /** Modo só-leitura (ex.: álbum "Conto"): sem selecionar/editar/mover/apagar/destacar. */
  somenteLeitura?: boolean;
}) {
  const queryClient = useQueryClient();

  /**
   * The replacement for `router.refresh()`.
   *
   * Next re-ran the server component and swapped in fresh HTML; here the
   * cached queries are invalidated and TanStack Query refetches. Same effect,
   * and the keys are the same pages the old code revalidated.
   */
  const recarregar = useCallback(() => {
    for (const key of AFTER_PHOTO_CHANGE) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }, [queryClient]);

  /** An API error already carries a Portuguese sentence; anything else doesn't. */
  const mensagemErro = (error: unknown, fallback: string) =>
    error instanceof ApiError ? error.message : fallback;
  // Sem controlos só no álbum "Conto", que é alimentado pela página do Conto.
  const soLeitura = somenteLeitura;
  // Guardamos a foto aberta por ID (não por índice): assim, apagar/editar/
  // destacar + router.refresh() nunca passam a apontar para a foto errada.
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [aGuardar, setAGuardar] = useState(false);
  const [aDestacar, setADestacar] = useState(false);
  const [aDescarregar, setADescarregar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Seleção múltipla (mover/apagar várias fotos de uma vez).
  const [modoSeleccao, setModoSeleccao] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [destino, setDestino] = useState("");
  const [aMover, setAMover] = useState(false);

  // Pedidos de confirmação pendentes (modal).
  const [confirmar, setConfirmar] = useState<
    | { tipo: "mover" }
    | { tipo: "apagarVarias" }
    | { tipo: "apagarUma"; id: string }
    | null
  >(null);

  // Estado de "a recarregar" depois de uma ação (escurece a grelha).
  const [aProcessar, iniciarProcesso] = useTransition();

  // Só fotos com URL assinado válido são mostráveis.
  const visiveis = fotos.filter((f) => f.src);

  function alternarSeleccao(id: string) {
    setSelecionadas((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  function sairSeleccao() {
    setModoSeleccao(false);
    setSelecionadas(new Set());
    setDestino("");
    setErro(null);
  }

  async function moverSelecionadas() {
    if (selecionadas.size === 0) return;
    setErro(null);
    setAMover(true);
    try {
      // `ids` is a real array now — it used to be a comma-joined string.
      await movePhotos({ ids: [...selecionadas], albumId: destino || null });
      setConfirmar(null);
      sairSeleccao();
      iniciarProcesso(recarregar);
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível mover as fotos."));
    } finally {
      setAMover(false);
    }
  }

  async function apagarSelecionadas() {
    if (selecionadas.size === 0) return;
    setErro(null);
    setAMover(true);
    try {
      await deletePhotos([...selecionadas]);
      setConfirmar(null);
      sairSeleccao();
      iniciarProcesso(recarregar);
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível apagar as fotos."));
    } finally {
      setAMover(false);
    }
  }

  /**
   * Guardar a foto no telemóvel/computador (IDEA-018).
   *
   * O URL é pedido no momento (dura 5 minutos) e já vem assinado com
   * `Content-Disposition: attachment` — por isso basta seguir a ligação: o
   * browser guarda o ficheiro sem sair da página. O atributo `download` do
   * `<a>` não serviria: é ignorado quando o ficheiro está noutro domínio
   * (o R2), e é precisamente esse o nosso caso.
   */
  async function descarregar(id: string) {
    setErro(null);
    setADescarregar(true);
    try {
      const { url } = await photoDownloadUrl(id);
      const ligacao = document.createElement("a");
      ligacao.href = url;
      ligacao.rel = "noopener";
      document.body.appendChild(ligacao);
      ligacao.click();
      ligacao.remove();
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível guardar a foto."));
    } finally {
      setADescarregar(false);
    }
  }

  async function apagarUma(id: string) {
    setErro(null);
    setAMover(true);
    try {
      await deletePhoto(id);
      setConfirmar(null);
      fechar();
      iniciarProcesso(recarregar);
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível apagar. Tenta novamente."));
    } finally {
      setAMover(false);
    }
  }
  const indiceAberto = abertaId === null ? -1 : visiveis.findIndex((f) => f.id === abertaId);
  const foto = indiceAberto >= 0 ? visiveis[indiceAberto] : null;

  const sairEdicao = useCallback(() => {
    setModoEdicao(false);
    setErro(null);
  }, []);

  const fechar = useCallback(() => {
    setAbertaId(null);
    sairEdicao();
  }, [sairEdicao]);
  const irPara = useCallback(
    (delta: number) => {
      sairEdicao();
      setAbertaId((id) => {
        if (id === null || visiveis.length === 0) return id;
        const i = visiveis.findIndex((f) => f.id === id);
        if (i < 0) return id;
        return visiveis[(i + delta + visiveis.length) % visiveis.length].id;
      });
    },
    [visiveis, sairEdicao],
  );
  const anterior = useCallback(() => irPara(-1), [irPara]);
  const seguinte = useCallback(() => irPara(1), [irPara]);

  async function guardarEdicao(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);
    setAGuardar(true);
    try {
      const fd = new FormData(e.currentTarget);
      await updatePhoto({
        id: String(fd.get("id") ?? ""),
        legenda: String(fd.get("legenda") ?? ""),
        marcoId: String(fd.get("marco_id") ?? "") || null,
        albumId: String(fd.get("album_id") ?? "") || null,
        // Só enviado quando o select existe mesmo. O campo do lugar não é
        // mostrado quando ainda não há lugares no mapa (ou quando a lista
        // ainda não chegou) — e aí `fd.get` devolve null, o que enviado como
        // `lugarId: null` apagaria silenciosamente a ligação da foto ao sítio.
        // Omitir a chave faz a API não lhe tocar.
        ...(fd.has("lugar_id") ? { lugarId: String(fd.get("lugar_id") ?? "") || null } : {}),
      });
      setModoEdicao(false);
      recarregar();
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível guardar."));
    } finally {
      setAGuardar(false);
    }
  }

  async function alternarDestaque(id: string) {
    setErro(null);
    setADestacar(true);
    try {
      await toggleFeatured(id);
      recarregar();
    } catch (error) {
      setErro(mensagemErro(error, "Não foi possível destacar a foto."));
    } finally {
      setADestacar(false);
    }
  }

  useEffect(() => {
    if (foto === null) return;
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (modoEdicao) sairEdicao();
        else fechar();
      } else if (!modoEdicao && e.key === "ArrowLeft") anterior();
      else if (!modoEdicao && e.key === "ArrowRight") seguinte();
    }
    window.addEventListener("keydown", aoTeclar);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = "";
    };
  }, [foto, fechar, anterior, seguinte, modoEdicao, sairEdicao]);

  return (
    <>
      {/* Barra de ferramentas: alternar modo de seleção (escondida em só-leitura) */}
      {!soLeitura && (
        <div className="mb-3 flex items-center justify-end">
          {modoSeleccao ? (
            <button
              type="button"
              onClick={sairSeleccao}
              className="rounded-full px-4 py-1.5 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200"
            >
              Cancelar seleção
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setModoSeleccao(true)}
              className="rounded-full border border-ouro/30 px-4 py-1.5 text-sm font-semibold text-ouro-600 transition-colors hover:bg-creme-200/60"
            >
              Selecionar
            </button>
          )}
        </div>
      )}

      <div
        className={`relative columns-2 gap-3 transition-opacity sm:columns-3 [&>*]:mb-3 ${
          aProcessar ? "pointer-events-none opacity-50" : ""
        }`}
      >
        {aProcessar && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-10">
            <span className="rounded-full bg-creme/90 px-4 py-2 text-sm font-semibold text-tinta-700 shadow">
              A atualizar…
            </span>
          </div>
        )}
        {visiveis.map((f) => {
          const marcada = selecionadas.has(f.id);
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => (modoSeleccao ? alternarSeleccao(f.id) : setAbertaId(f.id))}
              aria-pressed={modoSeleccao ? marcada : undefined}
              className={`group relative block w-full overflow-hidden rounded-2xl border shadow-sm transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ouro/50 ${
                modoSeleccao && marcada ? "border-ouro ring-2 ring-ouro" : "border-ouro/15"
              }`}
            >
              <img
                src={f.src!}
                alt={f.legenda ?? "Memória"}
                width={500}
                height={500}
                loading="lazy"
                decoding="async"
                className={`h-auto w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] ${
                  modoSeleccao && !marcada ? "opacity-80" : ""
                }`}
              />
              {modoSeleccao && (
                <span
                  aria-hidden
                  className={`absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold shadow ${
                    marcada ? "bg-ouro text-creme-50" : "bg-creme/90 text-tinta/50"
                  }`}
                >
                  {marcada ? "✓" : ""}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Barra de ação da seleção múltipla */}
      {modoSeleccao && (
        <div className="sticky bottom-3 z-30 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-ouro/25 bg-creme/95 px-4 py-3 shadow-lg backdrop-blur-sm">
          <span className="text-sm font-semibold text-tinta-700">
            {selecionadas.size}{" "}
            {selecionadas.size === 1 ? "foto selecionada" : "fotos selecionadas"}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              className="rounded-2xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none focus:border-ouro focus:ring-2 focus:ring-ouro/30"
            >
              <option value="">Álbum geral (sem pasta)</option>
              {albuns.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={aMover || selecionadas.size === 0}
              onClick={() => setConfirmar({ tipo: "mover" })}
              className="rounded-2xl bg-ouro px-5 py-2 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {aMover ? "A mover…" : "Mover para…"}
            </button>
            <button
              type="button"
              disabled={aMover || selecionadas.size === 0}
              onClick={() => setConfirmar({ tipo: "apagarVarias" })}
              className="rounded-2xl border border-blush/60 px-5 py-2 text-sm font-semibold text-tinta/80 transition-colors hover:bg-blush-300/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Apagar
            </button>
          </div>
          {erro && <p className="w-full text-sm text-tinta">{erro}</p>}
        </div>
      )}

      <AnimatePresence>
        {foto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={fechar}
            className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/80 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label={foto.legenda ?? "Memória"}
          >
            <button
              type="button"
              onClick={fechar}
              aria-label="Fechar"
              className="absolute right-4 top-4 rounded-full bg-creme/90 px-3 py-1.5 text-sm font-semibold text-tinta transition-colors hover:bg-creme"
            >
              ✕
            </button>

            {visiveis.length > 1 && !modoEdicao && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    anterior();
                  }}
                  aria-label="Anterior"
                  className="absolute left-3 rounded-full bg-creme/90 px-3 py-2 text-tinta transition-colors hover:bg-creme sm:left-6"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    seguinte();
                  }}
                  aria-label="Seguinte"
                  className="absolute right-3 rounded-full bg-creme/90 px-3 py-2 text-tinta transition-colors hover:bg-creme sm:right-6"
                >
                  ›
                </button>
              </>
            )}

            <motion.figure
              key={foto.id}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[88dvh] max-w-3xl flex-col items-center"
            >
              <img
                src={foto.src!}
                alt={foto.legenda ?? "Memória"}
                width={1200}
                height={1200}
                decoding="async"
                className="max-h-[70dvh] w-auto rounded-2xl object-contain shadow-2xl"
              />

              {modoEdicao && !soLeitura ? (
                <form
                  onSubmit={guardarEdicao}
                  className="mt-4 flex w-full max-w-md flex-col gap-3 rounded-2xl bg-creme/95 p-4 text-left shadow-xl"
                >
                  <input type="hidden" name="id" value={foto.id} />
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-tinta-700">Legenda</span>
                    <input
                      name="legenda"
                      defaultValue={foto.legenda ?? ""}
                      placeholder="Sem legenda"
                      className="rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none focus:border-ouro focus:ring-2 focus:ring-ouro/30"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-tinta-700">Ligar a um marco</span>
                    <select
                      name="marco_id"
                      defaultValue={foto.marco_id ?? ""}
                      className="rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none focus:border-ouro focus:ring-2 focus:ring-ouro/30"
                    >
                      <option value="">— Só no álbum —</option>
                      {marcos.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.titulo} ({dataLonga(m.data)})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-tinta-700">Pasta (álbum)</span>
                    <select
                      name="album_id"
                      defaultValue={foto.album_id ?? ""}
                      className="rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none focus:border-ouro focus:ring-2 focus:ring-ouro/30"
                    >
                      <option value="">— Sem pasta (álbum geral) —</option>
                      {albuns.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nome}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Onde foi tirada — é isto que enche as mini-galerias dos
                      pinos do mapa (IDEA-014). Só aparece quando já há lugares
                      marcados; um select vazio não ajudava ninguém. */}
                  {lugares.length > 0 && (
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-tinta-700">Lugar (mapa)</span>
                      <select
                        name="lugar_id"
                        defaultValue={foto.lugar_id ?? ""}
                        className="rounded-xl border border-ouro/30 bg-creme-50 px-3 py-2 text-sm text-tinta outline-none focus:border-ouro focus:ring-2 focus:ring-ouro/30"
                      >
                        <option value="">— Sem lugar —</option>
                        {lugares.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.icone} {l.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {erro && <p className="text-sm text-tinta">{erro}</p>}

                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={sairEdicao}
                      className="rounded-full px-4 py-1.5 text-sm font-semibold text-tinta-700 hover:bg-creme-200"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={aGuardar}
                      className="rounded-full bg-ouro px-5 py-1.5 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
                    >
                      {aGuardar ? "A guardar…" : "Guardar"}
                    </button>
                  </div>
                </form>
              ) : (
                <figcaption className="mt-3 flex flex-col items-center gap-2">
                  {foto.legenda && (
                    <span className="text-center text-sm text-creme">{foto.legenda}</span>
                  )}

                  {/* Guardar (IDEA-018) — fora do `soLeitura` de propósito:
                      vale também para as imagens do Conto. */}
                  <button
                    type="button"
                    disabled={aDescarregar}
                    onClick={() => descarregar(foto.id)}
                    className="rounded-full bg-creme/90 px-4 py-1.5 text-xs font-semibold text-tinta transition-colors hover:bg-creme disabled:opacity-60"
                  >
                    {aDescarregar ? "A guardar…" : "⬇ Guardar"}
                  </button>

                  {erro && (
                    <p role="alert" className="text-center text-xs text-creme">
                      {erro}
                    </p>
                  )}

                  {somenteLeitura ? (
                    <span className="text-xs text-creme/70">
                      Imagem do Conto — gere-a na página do Conto.
                    </span>
                  ) : soLeitura ? null : (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        disabled={aDestacar}
                        onClick={() => alternarDestaque(foto.id)}
                        aria-pressed={foto.destaque}
                        title={
                          foto.destaque
                            ? "Em destaque na página de aniversário"
                            : "Destacar na página de aniversário"
                        }
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${
                          foto.destaque
                            ? "bg-ouro text-creme-50 hover:bg-ouro-600"
                            : "bg-creme/90 text-tinta hover:bg-creme"
                        }`}
                      >
                        {aDestacar ? "A guardar…" : foto.destaque ? "★ Em destaque" : "☆ Destacar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setModoEdicao(true)}
                        className="rounded-full bg-creme/90 px-4 py-1.5 text-xs font-semibold text-tinta transition-colors hover:bg-creme"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmar({ tipo: "apagarUma", id: foto.id })}
                        className="rounded-full border border-blush/60 bg-creme/90 px-4 py-1.5 text-xs font-semibold text-tinta transition-colors hover:bg-blush-300/40"
                      >
                        Apagar
                      </button>
                    </div>
                  )}
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de confirmação para mover / apagar (IDEA-017) */}
      <Confirmacao
        aberto={confirmar !== null}
        aProcessar={aMover}
        destrutivo={confirmar?.tipo !== "mover"}
        titulo={confirmar?.tipo === "mover" ? "Mover fotos" : "Apagar fotos"}
        mensagem={
          confirmar?.tipo === "mover"
            ? `Mover ${selecionadas.size} ${
                selecionadas.size === 1 ? "foto" : "fotos"
              } para "${
                destino ? albuns.find((a) => a.id === destino)?.nome ?? "álbum" : "Álbum geral"
              }"?`
            : confirmar?.tipo === "apagarVarias"
              ? `Apagar ${selecionadas.size} ${
                  selecionadas.size === 1 ? "foto" : "fotos"
                }? São removidas do álbum e do armazenamento. Esta ação não pode ser desfeita.`
              : "Apagar esta foto? É removida do álbum e do armazenamento. Esta ação não pode ser desfeita."
        }
        textoConfirmar={confirmar?.tipo === "mover" ? "Mover" : "Apagar"}
        onCancelar={() => setConfirmar(null)}
        onConfirmar={() => {
          if (confirmar?.tipo === "mover") moverSelecionadas();
          else if (confirmar?.tipo === "apagarVarias") apagarSelecionadas();
          else if (confirmar?.tipo === "apagarUma") apagarUma(confirmar.id);
        }}
      />
    </>
  );
}
